import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {AuthService} from '../../../../services/auth.service';
import {NotificationService} from '../../../../services/notification.service';
import {ResponseObject} from '../../../../common/interfaces/global-interfaces';
import {UserManagementService} from '../../../../services/user-management.service';
import {StorageService} from '../../../../services/storage.service';
import { RegistrationService } from '../../../../services/registration.service';
import { OTPInputObject, ResendOTPMutation, VerificationTypeChoices } from '../../../../store/entities/uaa/auth-user.model';
import { passwordValidator } from '../../../validators/input-validators';


@Component({
  selector: 'app-staff-login',
  templateUrl: './staff-login.component.html',
  styleUrl: './staff-login.component.scss'
})
export class StaffLoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private uaaService = inject(UserManagementService);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private notificationService = inject(NotificationService);
  private registrationService = inject(RegistrationService);

  form: FormGroup;
  forgotForm: FormGroup;
  otpForm: FormGroup;
  resetForm: FormGroup;
  hide = true;
  loading = false;
  response: ResponseObject;
  showLogin = true;
  showOtpForm = false;
  loadingOtp = false;
  otpVerified = false;
  private otpTime = 5*60; //5 minutes
  otpTimeRemain = this.otpTime;
  intervalTimeOut: any;


  async ngOnInit(): Promise<void> {
    this.form = this.fb.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
    });

    this.forgotForm = this.fb.group({
      usePhone: [false, Validators.required],
      email: [null, Validators.email],
      phoneNumber: [null],
    });

    this.otpForm = this.fb.group({
      phoneNumber: [null, Validators.required],
      otp: [null, Validators.required],
    });

    this.resetForm = this.fb.group({
      phoneNumber: [null, Validators.required],
      password: [null, [Validators.required, Validators.pattern(passwordValidator)]],
      confirmPassword: [null, Validators.required],
    });
  }

  async login(formData: any) {
    this.loading = true;
    try {
      // login now
      const authTokenModel = await this.authService.login(formData);

      if (authTokenModel?.success) {
        await this.uaaService.authRole();
        if (this.authService.isStaffAccess()) {
          if (this.authService.isAdminAccess()) {
            console.log('redirect to admin');
            await this.router.navigate(['', 'manage', 'system-settings']);
          } else {
            const internalUserType: any = this.storageService.get('internalUserType');
            console.log('redirect to staff dashboard', internalUserType);
            await this.router.navigate(['', 'manage', 'institution-management', 'ma-institution']);
          }
        } else if (this.authService.isPortalAccess()) {
          const redirectUrl: string[] = this.storageService.get('redirectUrl');
          console.log('redirect to portal', redirectUrl);
          await this.router.navigate(redirectUrl?.length ? redirectUrl : ['', 'portal', 'portal-complaints', 'feedbacks']);
        }
      } else {
        this.notificationService.errorMessage(authTokenModel?.massage)
      }
    } catch (e) {
      console.error('Error fetching auth user:', e.message);
    }

    this.loading = false;
  }


  async forgotPassword (formData: any) {
    this.loading = true;
    if (formData?.usePhone) {
      this.otpForm.patchValue({
        phoneNumber: formData?.phoneNumber
      });
      this.resetForm.patchValue({
        phoneNumber: formData?.phoneNumber
      });
      this.startCounter();
      const verResponse = await this.registrationService?.verifyPhoneNumber(
        formData?.phoneNumber,
        VerificationTypeChoices.RESETPASSWORD
      );
      if (verResponse?.status) {
        this.showOtpForm = true;
      } else {
        this.notificationService.errorMessage(verResponse?.message);
      }
    } else {
      await this.authService.forgotPassword(formData);
    }
    this.loading = false;
  }

  async verifyOtp (formData: OTPInputObject) {
    this.loading = true;
    formData = {
      ...formData,
      otp: Number(formData?.otp)
    };
    const response = await this.registrationService.verifyOtp(formData);
    if (response?.status === true) {
      this.otpVerified = true;
      this.notificationService.successMessage(response?.message);
    } else {
      this.notificationService.errorMessage(response?.message);
    }
    this.loading = false;
  }


  async resendOtp () {
    this.loadingOtp = true;
    const phone: string = this.otpForm?.value?.phoneNumber;
    const data: ResendOTPMutation = await this.registrationService.resendOtp(phone);
    if (data?.response?.status) {
      this.otpTimeRemain = data?.resendTime;
    } else {
      this.notificationService.errorMessage(data?.response?.message);
    }
    this.loadingOtp = false;
  }

  private startCounter() {
    clearInterval(this.intervalTimeOut);
    this.intervalTimeOut = setInterval(() => {
      if (this.otpTimeRemain > 0) {
        this.otpTimeRemain -= 1;
      }
    }, 1000);
  }

  async resetPassword(formData: any) {
    this.loading = true;
    try {
      const response: ResponseObject = await this.authService.resetPassword({
        ...formData,
        confirmPassword: undefined,
      });
      if (response?.status === true) {
        this.notificationService.successMessage(response?.message);
        this.showLogin = true;
        this.showOtpForm = false;
        this.otpVerified = false;
      } else {
        this.notificationService.errorMessage(response?.message);
      }
    } catch (e) {
      console.log(e);
    }
    this.loading = false;
  }

  getMinutes(time: number): string {
    return Math.floor(time / 60).toString().padStart(2, '0');
  }

  getSeconds(time: number): string {
    return (time % 60).toString().padStart(2, '0');
  }

}
